import React from 'react';
import {
    Desktop20Regular,
    Code20Regular,
    Building20Regular,
    Organization20Regular,
    Search20Regular,
    Globe20Regular,
    Database20Regular,
    Wrench20Regular,
    DocumentData20Regular,
    ChartMultiple20Regular,
    Bot20Regular,
    DataUsage20Regular,
    TableSimple20Regular,
    DataTrending20Regular,  // Replace Analytics20Regular with DataTrending20Regular
    Settings20Regular,
    Brain20Regular,
    Target20Regular,
    Flash20Regular,
    Shield20Regular
} from '@fluentui/react-icons';
import { TeamService } from '@/services/TeamService';
import { TaskService } from '@/services';
import { iconMap } from '@/models/homeInput';
import { 
    TypeGuards, 
    DataSanitizer, 
    createErrorContext, 
    safePropertyAccess,
    type SafeAgent 
} from './agentMessageValidator';

// Extended icon mapping for user-uploaded string icons
const fluentIconMap: Record<string, React.ComponentType<any>> = {
    'Desktop20Regular': Desktop20Regular,
    'Code20Regular': Code20Regular,
    'Building20Regular': Building20Regular,
    'Organization20Regular': Organization20Regular,
    'Search20Regular': Search20Regular,
    'Globe20Regular': Globe20Regular,
    'Database20Regular': Database20Regular,
    'Wrench20Regular': Wrench20Regular,
    'DocumentData20Regular': DocumentData20Regular,
    'ChartMultiple20Regular': ChartMultiple20Regular,
    'Bot20Regular': Bot20Regular,
    'DataUsage20Regular': DataUsage20Regular,
    'TableSimple20Regular': TableSimple20Regular,
    'DataTrending20Regular': DataTrending20Regular,  // Updated
    'Analytics20Regular': DataTrending20Regular,     // Keep Analytics as alias
    'Settings20Regular': Settings20Regular,
    'Brain20Regular': Brain20Regular,
    'Target20Regular': Target20Regular,
    'Flash20Regular': Flash20Regular,
    'Shield20Regular': Shield20Regular,
    // Add common variations and aliases
    'desktop': Desktop20Regular,
    'code': Code20Regular,
    'building': Building20Regular,
    'organization': Organization20Regular,
    'search': Search20Regular,
    'globe': Globe20Regular,
    'database': Database20Regular,
    'wrench': Wrench20Regular,
    'document': DocumentData20Regular,
    'chart': ChartMultiple20Regular,
    'bot': Bot20Regular,
    'data': DataUsage20Regular,
    'table': TableSimple20Regular,
    'analytics': DataTrending20Regular,
    'trending': DataTrending20Regular,
    'settings': Settings20Regular,
    'brain': Brain20Regular,
    'target': Target20Regular,
    'flash': Flash20Regular,
    'shield': Shield20Regular
};

// Icon pool for unique assignment (excluding Person20Regular)
const AGENT_ICON_POOL = [
    Bot20Regular,
    DataTrending20Regular,  // Updated
    TableSimple20Regular,
    ChartMultiple20Regular,
    DataUsage20Regular,
    DocumentData20Regular,
    Settings20Regular,
    Brain20Regular,
    Target20Regular,
    Flash20Regular,
    Shield20Regular,
    Code20Regular,
    Search20Regular,
    Globe20Regular,
    Building20Regular,
    Organization20Regular,
    Wrench20Regular,
    Database20Regular,
    Desktop20Regular
];

// Cache for agent icon assignments to ensure consistency
const agentIconAssignments: Record<string, React.ComponentType<any>> = {};

/**
 * Error logger utility for agent icon operations
 */
class AgentIconErrorLogger {
  /**
   * Log error with context information
   */
  static logError(
    functionName: string,
    error: Error | string,
    context?: {
      agentName?: string;
      agentType?: string;
      originalData?: any;
      iconPath?: string;
    }
  ): void {
    const errorContext = createErrorContext('AgentIconUtils', functionName, {
      agentName: context?.agentName,
      agentType: context?.agentType,
      originalData: context?.originalData
    });

    const errorMessage = error instanceof Error ? error.message : error;
    
    console.error(`[AgentIconUtils.${functionName}] Error:`, {
      message: errorMessage,
      context: errorContext,
      iconPath: context?.iconPath,
      stack: error instanceof Error ? error.stack : undefined
    });
  }

  /**
   * Log warning with context information
   */
  static logWarning(
    functionName: string,
    message: string,
    context?: {
      agentName?: string;
      agentType?: string;
      fallbackUsed?: string;
    }
  ): void {
    const errorContext = createErrorContext('AgentIconUtils', functionName, {
      agentName: context?.agentName,
      agentType: context?.agentType
    });

    console.warn(`[AgentIconUtils.${functionName}] Warning:`, {
      message,
      context: errorContext,
      fallbackUsed: context?.fallbackUsed
    });
  }
}

/**
 * Generate a consistent hash from a string for icon assignment
 */
const generateHash = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
};

/**
 * Match user-uploaded string icon to Fluent UI component with robust error handling
 */
const matchStringToFluentIcon = (iconString: string): React.ComponentType<any> | null => {
    try {
        // Validate input with type guard - silently return null for non-strings
        if (!TypeGuards.isString(iconString) || iconString.trim() === '') {
            return null;
        }

        const sanitizedIconString = DataSanitizer.sanitizeAgentName(iconString);

        // Try exact match first
        if (fluentIconMap[sanitizedIconString]) {
            return fluentIconMap[sanitizedIconString];
        }

        // Try case-insensitive match
        const lowerIconString = sanitizedIconString.toLowerCase();
        if (fluentIconMap[lowerIconString]) {
            return fluentIconMap[lowerIconString];
        }

        // Try removing common suffixes and prefixes
        const cleanedIconString = sanitizedIconString
            .replace(/20Regular$/i, '')
            .replace(/Regular$/i, '')
            .replace(/20$/i, '')
            .replace(/^fluent/i, '')
            .replace(/^icon/i, '')
            .toLowerCase()
            .trim();

        if (fluentIconMap[cleanedIconString]) {
            return fluentIconMap[cleanedIconString];
        }

        // Silently return null for unmatched icons - this is expected behavior
        // when agents don't have custom icons configured
        return null;
    } catch (error) {
        AgentIconErrorLogger.logError('matchStringToFluentIcon', error as Error, {
            originalData: iconString
        });
        return null;
    }
};

/**
 * Get deterministic icon for agent based on name pattern matching with error handling
 * This ensures agents with the same name always get the same icon
 */
const getDeterministicAgentIcon = (cleanName: string): React.ComponentType<any> => {
    try {
        // Validate input
        if (!TypeGuards.isString(cleanName)) {
            AgentIconErrorLogger.logWarning('getDeterministicAgentIcon', 
                'Invalid clean name provided, using fallback', {
                fallbackUsed: 'Bot20Regular'
            });
            return Bot20Regular;
        }

        const safeName = cleanName.toLowerCase().trim();
        
        // Pattern-based assignment - deterministic based on agent name
        if (safeName.includes('data') && safeName.includes('order')) {
            return TableSimple20Regular;
        } else if (safeName.includes('data') && safeName.includes('customer')) {
            return DataUsage20Regular;
        } else if (safeName.includes('analysis') || safeName.includes('recommend') || safeName.includes('insight')) {
            return DataTrending20Regular;
        } else if (safeName.includes('proxy') || safeName.includes('interface')) {
            return Bot20Regular;
        } else if (safeName.includes('brain') || safeName.includes('ai') || safeName.includes('intelligence')) {
            return Brain20Regular;
        } else if (safeName.includes('security') || safeName.includes('protect') || safeName.includes('guard')) {
            return Shield20Regular;
        } else if (safeName.includes('target') || safeName.includes('goal') || safeName.includes('objective')) {
            return Target20Regular;
        } else if (safeName.includes('fast') || safeName.includes('quick') || safeName.includes('speed')) {
            return Flash20Regular;
        } else if (safeName.includes('code') || safeName.includes('dev') || safeName.includes('program')) {
            return Code20Regular;
        } else if (safeName.includes('search') || safeName.includes('find') || safeName.includes('lookup')) {
            return Search20Regular;
        } else if (safeName.includes('web') || safeName.includes('internet') || safeName.includes('online')) {
            return Globe20Regular;
        } else if (safeName.includes('business') || safeName.includes('company') || safeName.includes('enterprise')) {
            return Building20Regular;
        } else if (safeName.includes('hr') || safeName.includes('human') || safeName.includes('people')) {
            return Organization20Regular;
        } else if (safeName.includes('tool') || safeName.includes('utility') || safeName.includes('helper')) {
            return Wrench20Regular;
        } else if (safeName.includes('document') || safeName.includes('file') || safeName.includes('report')) {
            return DocumentData20Regular;
        } else if (safeName.includes('config') || safeName.includes('setting') || safeName.includes('manage')) {
            return Settings20Regular;
        } else if (safeName.includes('data') || safeName.includes('database')) {
            return Database20Regular;
        } else {
            // Use hash-based assignment for consistent selection across identical names
            const hash = generateHash(safeName);
            const iconIndex = hash % AGENT_ICON_POOL.length;
            return AGENT_ICON_POOL[iconIndex];
        }
    } catch (error) {
        AgentIconErrorLogger.logError('getDeterministicAgentIcon', error as Error, {
            originalData: cleanName
        });
        return Bot20Regular; // Consistent fallback
    }
};

/**
 * Get unique icon for an agent based on their name and context with robust error handling
 * Ensures agents with identical names get identical icons
 */
const getUniqueAgentIcon = (
    agentName: string,
    allAgentNames: string[],
    iconStyle: React.CSSProperties
): React.ReactNode => {
    try {
        // Validate inputs with type guards
        const safeName = DataSanitizer.sanitizeAgentName(agentName);
        const safeStyle = iconStyle || { fontSize: '16px', color: 'var(--colorNeutralForeground2)' };
        
        if (!safeName) {
            AgentIconErrorLogger.logWarning('getUniqueAgentIcon', 
                'Empty agent name provided, using fallback', {
                agentName: agentName,
                fallbackUsed: 'Bot20Regular'
            });
            return React.createElement(Bot20Regular, { style: safeStyle });
        }

        const cleanName = TaskService?.cleanTextToSpaces ? 
            TaskService.cleanTextToSpaces(safeName).toLowerCase() : 
            safeName.toLowerCase();

        // If we already assigned an icon to this agent, use it
        if (agentIconAssignments[cleanName]) {
            const IconComponent = agentIconAssignments[cleanName];
            try {
                return React.createElement(IconComponent, { style: safeStyle });
            } catch (renderError) {
                AgentIconErrorLogger.logError('getUniqueAgentIcon', renderError as Error, {
                    agentName: safeName,
                    originalData: { cleanName, iconComponent: IconComponent.name }
                });
                // Remove corrupted assignment and continue with fresh assignment
                delete agentIconAssignments[cleanName];
            }
        }

        // Get deterministic icon based on agent name patterns
        // This ensures same names always get the same icon regardless of assignment order
        const selectedIcon = getDeterministicAgentIcon(cleanName);

        // Cache the assignment for future lookups
        agentIconAssignments[cleanName] = selectedIcon;

        return React.createElement(selectedIcon, { style: safeStyle });
    } catch (error) {
        AgentIconErrorLogger.logError('getUniqueAgentIcon', error as Error, {
            agentName: agentName,
            originalData: { allAgentNames, iconStyle }
        });
        
        // Return fallback icon with safe style
        const fallbackStyle = { fontSize: '16px', color: 'var(--colorNeutralForeground2)' };
        return React.createElement(Bot20Regular, { style: fallbackStyle });
    }
};

/**
 * Comprehensive utility function to get agent icon from multiple data sources
 * with consistent fallback patterns and robust error handling across all components
 */
export const getAgentIcon = (
    agentName: string,
    planData?: any,
    planApprovalRequest?: any,
    iconColor: string = 'var(--colorNeutralForeground2)'
): React.ReactNode => {
    try {
        // Validate and sanitize inputs
        const safeName = DataSanitizer.sanitizeAgentName(agentName);
        const safeIconColor = TypeGuards.isString(iconColor) ? iconColor : 'var(--colorNeutralForeground2)';
        const iconStyle = { fontSize: '16px', color: safeIconColor };

        if (!safeName) {
            AgentIconErrorLogger.logWarning('getAgentIcon', 
                'Empty or invalid agent name provided', {
                agentName: agentName,
                fallbackUsed: 'Bot20Regular'
            });
            return React.createElement(Bot20Regular, { style: iconStyle });
        }

        // 1. First priority: Get from uploaded team configuration in planData
        if (planData?.team?.agents && TypeGuards.isArray(planData.team.agents)) {
            try {
                const cleanAgentName = TaskService?.cleanTextToSpaces ? 
                    TaskService.cleanTextToSpaces(safeName) : safeName;

                const agent = planData.team.agents.find((a: any) => {
                    if (!a || typeof a !== 'object') return false;
                    
                    const agentName = safePropertyAccess(a, 'name', '');
                    const agentType = safePropertyAccess(a, 'type', '') || safePropertyAccess(a, 'agent_type', '');
                    const inputKey = safePropertyAccess(a, 'input_key', '');
                    
                    const cleanName = TaskService?.cleanTextToSpaces ? 
                        TaskService.cleanTextToSpaces(agentName) : agentName;
                    const cleanType = TaskService?.cleanTextToSpaces ? 
                        TaskService.cleanTextToSpaces(agentType) : agentType;
                    const cleanInputKey = TaskService?.cleanTextToSpaces ? 
                        TaskService.cleanTextToSpaces(inputKey) : inputKey;

                    return cleanName.toLowerCase().includes(cleanAgentName.toLowerCase()) ||
                           cleanType.toLowerCase().includes(cleanAgentName.toLowerCase()) ||
                           cleanInputKey.toLowerCase().includes(cleanAgentName.toLowerCase());
                });

                const agentIcon = safePropertyAccess(agent, 'icon', '');
                if (agentIcon) {
                    // Try to match string to Fluent icon component first
                    const FluentIconComponent = matchStringToFluentIcon(agentIcon);
                    if (FluentIconComponent) {
                        return React.createElement(FluentIconComponent, { style: iconStyle });
                    }

                    // Fallback: check if it's in the existing iconMap
                    if (iconMap && iconMap[agentIcon]) {
                        try {
                            return React.cloneElement(iconMap[agentIcon] as React.ReactElement, {
                                style: iconStyle
                            });
                        } catch (cloneError) {
                            AgentIconErrorLogger.logError('getAgentIcon', cloneError as Error, {
                                agentName: safeName,
                                iconPath: 'planData.iconMap'
                            });
                        }
                    }
                }
            } catch (planDataError) {
                AgentIconErrorLogger.logError('getAgentIcon', planDataError as Error, {
                    agentName: safeName,
                    iconPath: 'planData.team.agents'
                });
            }
        }

        // 2. Second priority: Get from stored team configuration (TeamService)
        try {
            const storedTeam = TeamService?.getStoredTeam ? TeamService.getStoredTeam() : null;
            if (storedTeam?.agents && TypeGuards.isArray(storedTeam.agents)) {
                const cleanAgentName = TaskService?.cleanTextToSpaces ? 
                    TaskService.cleanTextToSpaces(safeName) : safeName;

                const agent = storedTeam.agents.find((a: any) => {
                    if (!a || typeof a !== 'object') return false;
                    
                    // FIX: Use safe property access with optional chaining for the undefined type bug
                    const agentName = safePropertyAccess(a, 'name', '');
                    const agentType = safePropertyAccess(a, 'type', '') || safePropertyAccess(a, 'agent_type', '');
                    const inputKey = safePropertyAccess(a, 'input_key', '');
                    
                    const cleanName = TaskService?.cleanTextToSpaces ? 
                        TaskService.cleanTextToSpaces(agentName) : agentName;
                    const cleanType = TaskService?.cleanTextToSpaces ? 
                        TaskService.cleanTextToSpaces(agentType) : agentType;
                    const cleanInputKey = TaskService?.cleanTextToSpaces ? 
                        TaskService.cleanTextToSpaces(inputKey) : inputKey;

                    return cleanName.toLowerCase().includes(cleanAgentName.toLowerCase()) ||
                           cleanType.toLowerCase().includes(cleanAgentName.toLowerCase()) ||
                           cleanInputKey.toLowerCase().includes(cleanAgentName.toLowerCase());
                });

                const agentIcon = safePropertyAccess(agent, 'icon', '');
                if (agentIcon && iconMap && iconMap[agentIcon]) {
                    try {
                        return React.cloneElement(iconMap[agentIcon] as React.ReactElement, {
                            style: iconStyle
                        });
                    } catch (cloneError) {
                        AgentIconErrorLogger.logError('getAgentIcon', cloneError as Error, {
                            agentName: safeName,
                            iconPath: 'storedTeam.iconMap'
                        });
                    }
                }
            }
        } catch (storedTeamError) {
            AgentIconErrorLogger.logError('getAgentIcon', storedTeamError as Error, {
                agentName: safeName,
                iconPath: 'TeamService.getStoredTeam'
            });
        }

        // 3. Third priority: Get from participant_descriptions in planApprovalRequest
        try {
            const participantDesc = safePropertyAccess(
                planApprovalRequest, 
                'context.participant_descriptions.' + safeName, 
                null
            );
            
            const participantIcon = safePropertyAccess(participantDesc, 'icon', '');
            if (participantIcon && iconMap && iconMap[participantIcon]) {
                try {
                    return React.cloneElement(iconMap[participantIcon] as React.ReactElement, {
                        style: iconStyle
                    });
                } catch (cloneError) {
                    AgentIconErrorLogger.logError('getAgentIcon', cloneError as Error, {
                        agentName: safeName,
                        iconPath: 'planApprovalRequest.participant_descriptions'
                    });
                }
            }
        } catch (participantError) {
            AgentIconErrorLogger.logError('getAgentIcon', participantError as Error, {
                agentName: safeName,
                iconPath: 'planApprovalRequest.context.participant_descriptions'
            });
        }

        // 4. Deterministic icon assignment - ensures same names get same icons
        // Get all agent names from current context for unique assignment
        let allAgentNames: string[] = [];

        try {
            if (planApprovalRequest?.team && TypeGuards.isArray(planApprovalRequest.team)) {
                allAgentNames = planApprovalRequest.team;
            } else if (planData?.team?.agents && TypeGuards.isArray(planData.team.agents)) {
                allAgentNames = planData.team.agents.map((a: any) => 
                    safePropertyAccess(a, 'name', '') || 
                    safePropertyAccess(a, 'type', '') || 
                    safePropertyAccess(a, 'agent_type', '') || 
                    ''
                ).filter((name: string) => name.trim() !== '');
            } else {
                // Try to get stored team again for agent names
                const storedTeamForNames = TeamService?.getStoredTeam ? TeamService.getStoredTeam() : null;
                if (storedTeamForNames?.agents && TypeGuards.isArray(storedTeamForNames.agents)) {
                    allAgentNames = storedTeamForNames.agents.map((a: any) => 
                        safePropertyAccess(a, 'name', '')
                    ).filter((name: string) => name.trim() !== '');
                }
            }
        } catch (namesError) {
            AgentIconErrorLogger.logError('getAgentIcon', namesError as Error, {
                agentName: safeName,
                iconPath: 'allAgentNames extraction'
            });
            allAgentNames = [];
        }

        return getUniqueAgentIcon(safeName, allAgentNames, iconStyle);
    } catch (error) {
        AgentIconErrorLogger.logError('getAgentIcon', error as Error, {
            agentName: agentName,
            originalData: { planData, planApprovalRequest, iconColor }
        });
        
        // Return consistent fallback icon
        const fallbackStyle = { fontSize: '16px', color: 'var(--colorNeutralForeground2)' };
        return React.createElement(Bot20Regular, { style: fallbackStyle });
    }
};

/**
 * Clear agent icon assignments (useful when switching teams/contexts) with error handling
 */
export const clearAgentIconAssignments = (): void => {
    try {
        Object.keys(agentIconAssignments).forEach(key => {
            delete agentIconAssignments[key];
        });
        
        console.log('[AgentIconUtils] Cleared agent icon assignments');
    } catch (error) {
        AgentIconErrorLogger.logError('clearAgentIconAssignments', error as Error);
    }
};

/**
 * Get current agent icon assignments for debugging
 */
export const getAgentIconAssignments = (): Record<string, string> => {
    try {
        const assignments: Record<string, string> = {};
        Object.entries(agentIconAssignments).forEach(([key, component]) => {
            assignments[key] = component.name || 'Unknown';
        });
        return assignments;
    } catch (error) {
        AgentIconErrorLogger.logError('getAgentIconAssignments', error as Error);
        return {};
    }
};

/**
 * Validate agent icon utility health
 */
export const validateAgentIconUtilsHealth = (): { 
    isHealthy: boolean; 
    errors: string[]; 
    warnings: string[] 
} => {
    const errors: string[] = [];
    const warnings: string[] = [];

    try {
        // Check if required dependencies are available
        if (!TaskService?.cleanTextToSpaces) {
            warnings.push('TaskService.cleanTextToSpaces is not available');
        }

        if (!TeamService?.getStoredTeam) {
            warnings.push('TeamService.getStoredTeam is not available');
        }

        if (!iconMap) {
            warnings.push('iconMap is not available');
        }

        // Check if icon pool is valid
        if (!TypeGuards.isArray(AGENT_ICON_POOL) || AGENT_ICON_POOL.length === 0) {
            errors.push('AGENT_ICON_POOL is invalid or empty');
        }

        // Check if fluent icon map is valid
        if (!fluentIconMap || Object.keys(fluentIconMap).length === 0) {
            errors.push('fluentIconMap is invalid or empty');
        }

        return {
            isHealthy: errors.length === 0,
            errors,
            warnings
        };
    } catch (error) {
        AgentIconErrorLogger.logError('validateAgentIconUtilsHealth', error as Error);
        return {
            isHealthy: false,
            errors: ['Health check failed with exception'],
            warnings: []
        };
    }
};

/**
 * Get agent display name with proper formatting and robust error handling
 * Removes redundant "Agent" suffix and handles proper spacing
 */
export const getAgentDisplayName = (agentName: string): string => {
    try {
        // Validate and sanitize input
        const safeName = DataSanitizer.sanitizeAgentName(agentName);
        
        if (!safeName || safeName.trim() === '') {
            return 'Assistant';
        }

        // Clean and format the name
        let cleanName = TaskService?.cleanTextToSpaces ? 
            TaskService.cleanTextToSpaces(safeName) : safeName;

        // Remove "Agent" suffix if it exists (case insensitive)
        cleanName = cleanName.replace(/\s*agent\s*$/gi, '').trim();

        // Convert to proper case
        cleanName = cleanName
            .replace(/\bHRHelper\b/gi, 'HR Helper')
            .replace(/\bHR([A-Z])/g, 'HR $1')        // Add space after HR before capital letter
            .replace(/\bIT([A-Z])/g, 'IT $1')        // Add space after IT before capital letter
            .replace(/\bAPI([A-Z])/g, 'API $1');     // Add space after API before capital letter

        // Convert to proper case
        cleanName = cleanName.replace(/\b\w/g, l => l.toUpperCase());

        // Handle special cases for better readability
        cleanName = cleanName
            .replace(/\bKb\b/g, 'KB')          // KB instead of Kb
            .replace(/\bApi\b/g, 'API')        // API instead of Api
            .replace(/\bHr\b/g, 'HR')          // HR instead of Hr
            .replace(/\bIt\b/g, 'IT')          // IT instead of It
            .replace(/\bAi\b/g, 'AI')          // AI instead of Ai
            .replace(/\bUi\b/g, 'UI')          // UI instead of Ui
            .replace(/\bDb\b/g, 'DB');         // DB instead of Db

        return cleanName || 'Assistant';
    } catch (error) {
        AgentIconErrorLogger.logError('getAgentDisplayName', error as Error, {
            agentName: agentName
        });
        return 'Assistant';
    }
};

/**
 * Get agent display name with "Agent" suffix for display purposes with error handling
 */
export const getAgentDisplayNameWithSuffix = (agentName: string): string => {
    try {
        const baseName = getAgentDisplayName(agentName);
        return `${baseName} Agent`;
    } catch (error) {
        AgentIconErrorLogger.logError('getAgentDisplayNameWithSuffix', error as Error, {
            agentName: agentName
        });
        return 'Assistant Agent';
    }
};

/**
 * Get agent icon with custom styling override and comprehensive error handling
 */
export const getStyledAgentIcon = (
    agentName: string,
    customStyle: React.CSSProperties,
    planData?: any,
    planApprovalRequest?: any
): React.ReactNode => {
    try {
        // Validate inputs
        const safeName = DataSanitizer.sanitizeAgentName(agentName);
        const safeStyle = customStyle || {};
        
        if (!safeName) {
            AgentIconErrorLogger.logWarning('getStyledAgentIcon', 
                'Empty agent name provided', {
                agentName: agentName,
                fallbackUsed: 'Bot20Regular'
            });
            return React.createElement(Bot20Regular, { style: safeStyle });
        }

        const icon = getAgentIcon(safeName, planData, planApprovalRequest);

        if (React.isValidElement(icon)) {
            try {
                // Safely merge styles with validation
                const existingStyle = (icon.props as any)?.style || {};
                const mergedStyle = {
                    ...existingStyle,
                    ...safeStyle
                };

                return React.cloneElement(icon as React.ReactElement<any>, {
                    style: mergedStyle
                });
            } catch (cloneError) {
                AgentIconErrorLogger.logError('getStyledAgentIcon', cloneError as Error, {
                    agentName: safeName,
                    originalData: { customStyle, iconType: typeof icon }
                });
                
                // Fallback: return original icon if cloning fails
                return icon;
            }
        }

        return icon;
    } catch (error) {
        AgentIconErrorLogger.logError('getStyledAgentIcon', error as Error, {
            agentName: agentName,
            originalData: { customStyle, planData, planApprovalRequest }
        });
        
        // Return fallback icon with custom style
        const fallbackStyle = { 
            fontSize: '16px', 
            color: 'var(--colorNeutralForeground2)',
            ...customStyle 
        };
        return React.createElement(Bot20Regular, { style: fallbackStyle });
    }
};